import axios from "axios";
import {
  DeleteAccountDto,
  GetApiIntegrationDto,
  PublicUserDto,
  ResetPasswordDto,
  UpdateUserDto,
  UserDto,
} from "./users.types";
import {
  API_URL_GOOGLE,
  API_URL_MICROSOFT,
  API_URL_USERS,
  AvailableAPIProvidersType,
} from "../apiURLS";

const getMe = async (): Promise<UserDto> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = await axios.get<UserDto>(`${API_URL_USERS}/me`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    withCredentials: true,
  });
  if (response) return response.data;
  throw new Error("No data received.");
};

const getAPIIntegrations = async (): Promise<GetApiIntegrationDto[]> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = await axios.get<GetApiIntegrationDto[]>(
    `${API_URL_USERS}/api-integrations`,
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      withCredentials: true,
    },
  );
  if (response) return response.data;
  throw new Error("No data received.");
};

const registerAPIIntegration = async (
  providerToken: string,
  provider: AvailableAPIProvidersType,
  codeVerifier: string,
  redirectUri?: string,
): Promise<GetApiIntegrationDto[]> => {
  const authToken = localStorage.getItem("daex-auth-token");

  switch (provider) {
    case "google": {
      return (
        await axios.post(
          API_URL_GOOGLE + "/registerAPIAccess",
          {
            providerToken: providerToken,
            codeVerifier,
            redirectUri,
          },
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
            withCredentials: true,
          },
        )
      ).data;
    }
    case "microsoft": {
      return (
        await axios.post(
          API_URL_MICROSOFT + "/registerAPIAccess",
          {
            providerToken: providerToken,
            codeVerifier: codeVerifier,
            redirectUri,
          },
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
            withCredentials: true,
          },
        )
      ).data;
    }
    default:
      return [];
  }
};

const removeAPIIntegration = async (id: string): Promise<string> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = await axios.delete<string>(
    `${API_URL_USERS}/api-integration/${id}`,
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      withCredentials: true,
    },
  );
  if (response) return response.data;
  throw new Error("No data received.");
};

const verifyAccount = async (verifyToken: string): Promise<UserDto> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = await axios.post<UserDto>(
    `${API_URL_USERS}/verify-account`,
    {
      verifyToken,
    },
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response.data;
};

const requestVerificationToken = async (): Promise<boolean> => {
  const authToken = localStorage.getItem("daex-auth-token");

  await axios.get(`${API_URL_USERS}/request-account-verification`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    withCredentials: true,
  });
  return true;
};

const updateUserData = async (userData: UpdateUserDto): Promise<UserDto> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = await axios.put(
    `${API_URL_USERS}/${userData.id}`,
    userData,
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      withCredentials: true,
    },
  );
  return response.data;
};

const getPublicUser = async (userName: string): Promise<PublicUserDto> => {
  const authToken = localStorage.getItem("daex-auth-token");

  const response = await axios.get(`${API_URL_USERS}/get/${userName}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    withCredentials: true,
  });
  return response.data;
};

const requestPasswordResetToken = async (email: string): Promise<void> => {
  await axios.post(`${API_URL_USERS}/request-password-reset`, {
    email,
  });
};

const requestAccountDeletion = async (email: string): Promise<void> => {
  await axios.post(`${API_URL_USERS}/request-account-deletion`, {
    email,
  });
};

const deleteAccount = async (data: DeleteAccountDto): Promise<void> => {
  await axios.post(`${API_URL_USERS}/delete-account`, {
    token: data.deleteToken,
    email: data.email,
  });
};

const resetPassword = async (
  resetPasswordData: ResetPasswordDto,
): Promise<void> => {
  await axios.post(`${API_URL_USERS}/reset-password`, {
    ...resetPasswordData,
  });
};

const usersService = {
  getMe,
  verifyAccount,
  requestVerificationToken,
  resetPassword,
  requestPasswordResetToken,
  updateUserData,
  getAPIIntegrations,
  removeAPIIntegration,
  registerAPIIntegration,
  getPublicUser,
  requestAccountDeletion,
  deleteAccount,
};
export default usersService;
